import { Schema } from "../services/validation.service";

export const acceptedMediaExtensions = {
  image: ['.gif', '.jpg', '.jpeg', '.png', '.heic'],
  video: ['.mov', '.mp4', '.mpeg', '.webm', '.ogg'],
  audio: ['.mp3', '.wav', '.ogg']
};

export const timeData = {
  periods: [ 'Once', 'Daily', 'Weekly', 'BiWeekly', 'Monthly' ] as const,
  weekdays: [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' 
  ] as const,
  months: [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
  ] as const,
  daysPerMonth: [
    31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31
  ] as const,
  years: [...[...Array(40).keys()].map(y => y+2020)] as const,
  dates: [...Array(32).keys()] as const,
  times: [...[...Array(24).keys()].reduce<string[]>((a,h)=> [
    ...a, 
    `${(h%12 || 12).toString().padStart(2,'0')}:00${h < 12 ? 'am' : 'pm'}`,
    `${(h%12 || 12).toString().padStart(2,'0')}:15${h < 12 ? 'am' : 'pm'}`,
    `${(h%12 || 12).toString().padStart(2,'0')}:30${h < 12 ? 'am' : 'pm'}`,
    `${(h%12 || 12).toString().padStart(2,'0')}:45${h < 12 ? 'am' : 'pm'}`,
  ], [])] as const
};

const { periods, weekdays, months, daysPerMonth, years, dates, times } = timeData;

export type User = {
  id: number,
  username: string, 
  privilege: string,
  avatar: string,
}

export type UpdateType = {
  id: number,
  // userID: number,
  // timestamp: number,
  subject: string,
  date: string,
  update: string
}

export type Contact = {
  id: number,
  email: string,
  subject: string,
  message: string,
  timestamp: number,
  search: string
}

export type EventPerformance = {
  id: number,
  day: typeof dates[number];
  month: typeof months[number];
  year: typeof years[number];
  time: typeof times[number];
  timestamp: number,
  period: typeof periods[number];
  addressa: string,
  addressb: string,
  location: string,
  thumbnail: string,
  description: string,
  website: string,
  media: string[]
}

export const EventPerformanceSchema: Schema = {
  day: { type: 'number', attributes: { required: true, range: { min: 1, max: 31 } } },
  month: { type: [...months], attributes: { required: true } },
  year: { type: 'number', attributes: { required: true, range: { min: 2020, max: 2120 } } },
  time: { type: [...times], attributes: { required: true }},
  location: { type: 'string', attributes: { required: true } },
  addressa: { type: 'string', attributes: { required: true } },
  addressb: { type: 'string', attributes: { required: true } },
  thumbnail: { type: 'string', attributes: { required: false } },
  description: { type: 'string', attributes: { required: true, strLength: { minLength: 5 } }},
  website: { type: 'string', attributes: { required: false }},
  period: { type: [...periods], attributes: { required: true } },
  media: { type: 'string', attributes: { required: false, array: { minLength: 0 }}}
};

export type Review = {
  id: number,
  event: number,
  timestamp: number,
  approved: string,
  name: string,
  stars: number,
  text: string,
}

export type Mail = {
  id: number,
  email: string,
  // code: string,
  // salt: string,
  verified: string
}

export type FontFam = | { src: string; family?: never } | { src?: never; family: string };

export type Theme = {
  id: number,
  herovideo: string,
  herotext: string,
  heroimage: string,
  threecard1title: string,
  threecard1image: string,
  threecard1text: string,
  threecard2title: string,
  threecard2image: string,
  threecard2text: string,
  threecard3title: string,
  threecard3image: string,
  threecard3text: string,
  about: string,
  card1color: string,
  card2color: string,
  font: string,
  cards: {
      primary: string;
      secondary: string;
      tertiary: string;
  },
  forms: {
      primary: string;
      secondary: string;
      tertiary: string;
  },
  buttons: {
      primary: string;
      secondary: string;
      tertiary: string;
  },
  fonts: {
      primary: FontFam;
      secondary: FontFam;
      tertiary: FontFam;
  }
}

export const themeSchema: Schema = {
  id: { type: 'string | number', attributes: { required: false }},
  herovideo: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  heroimage: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  herotext: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }}},
  threecard1title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard1image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard1text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }}},
  threecard2title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard2image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard2text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }}},
  threecard3title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard3image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard3text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }}},
  about: { type: 'string', attributes: { required: true, strLength: { maxLength: 8192 }}},
  card1color: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  card2color: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  font: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  cards: {
    type: {
      primary: { type: 'string', attributes: { required: true }},
      secondary: { type: 'string', attributes: { required: true }},
      tertiary: { type: 'string', attributes: { required: true }},
    },
    attributes: { required: true }
  },
  forms: {
    type: {
      primary: { type: 'string', attributes: { required: true }},
      secondary: { type: 'string', attributes: { required: true }},
      tertiary: { type: 'string', attributes: { required: true }},
    },
    attributes: { required: true }
  },
  buttons: {
    type: {
      primary: { type: 'string', attributes: { required: true }},
      secondary: { type: 'string', attributes: { required: true }},
      tertiary: { type: 'string', attributes: { required: true }},
    },
    attributes: { required: true }
  },
}

export const defaultTheme: Theme = {
  id: -1,
  herotext: `Get ready for ZAE – the driving force behind unforgettable house music moments. With an unparalleled ability to fuse tradition and innovation, ZAE commands dancefloors, creating a rhythmic unity that transcends. From underground vibes to festival stages, experience the pulse of ZAE's curated sonic journey that redefines house music, one set at a time.`,
  herovideo: `media/IMG_0358.mp4`,
  heroimage: `media/IMG_0360_100.jpg`,
  threecard1title: `Great Atmosphere`,
  threecard1image: `media/IMG_4329.JPG`,
  threecard1text: `ZAE, a master of house music, skillfully crafts mesmerizing atmospheres in any venue. Through seamless track blending and flawless transitions, ZAE turns ordinary spaces into pulsating realms of sonic bliss. With an intuitive grasp of the crowd's energy, ZAE curates a musical journey that elevates the atmosphere, transforming the dance floor into a unique celebration of rhythm and sound.`,
  threecard2title: `Great Music`,
  threecard2image: `media/IMG_4315.JPG`,
  threecard2text: `Renowned for their expertise in house music, ZAE showcases a remarkable talent for curating unforgettable atmospheres through an expertly crafted playlist. Each track is thoughtfully selected, creating a seamless blend that transforms any venue into a pulsating realm of sonic bliss. With an intuitive understanding of the crowd's vibe, ZAE's curated musical journey elevates the atmosphere, turning the dance floor into a distinctive celebration of rhythm and sound.`,
  threecard3title: `Great Crowds`,
  threecard3image: `media/crowd2.jpg`,
  threecard3text: `ZAE's magnetic music draws a sizable, energetic crowd to the dance floor, creating an inclusive atmosphere where attendees feel intimately connected to the beats. The lively tapestry of movement unfolds as ZAE skillfully engages the crowd, turning each event into a dynamic celebration where the music acts as a unifying force, transcending boundaries and inviting everyone to revel in the collective joy of the moment.`,
  about: `    In the heart of Milwaukee's vibrant music scene, I found my calling and passion as a house music DJ. Growing up surrounded by the rhythm of the city, I was captivated by the pulsating beats that echoed through the streets. From the moment I first stepped into a dimly lit nightclub and felt the music reverberate through my soul, I knew I had discovered my truest form of expression.
    My journey into the world of DJing started in the most unexpected way. As a teenager, I would spend hours exploring record stores, searching for that perfect beat that would ignite the dance floor. With each vinyl I collected, I honed my skills, learning the art of blending tracks seamlessly. The turntables became an extension of myself, allowing me to communicate my emotions and energy to the crowd.
    Milwaukee, with its diverse and passionate music lovers, became my canvas. I would spin at local clubs, from the underground gems to the mainstream venues, infusing every set with my unique style. My mixes were more than just a collection of tracks; they were a narrative, a journey that took the listeners on a sonic adventure. The city responded with open arms, embracing the fusion of classic house beats and innovative sounds that became my signature.
    Being a female DJ in a male-dominated industry had its challenges, but it only fueled my determination. I wanted to break barriers, challenge stereotypes, and show the world that music knows no gender. Milwaukee became the backdrop for my story of resilience and passion. With every beat I dropped, I hoped to inspire others to follow their dreams fearlessly, regardless of societal expectations.
    As the city's nightlife pulsed around me, I felt a deep connection to the community. Milwaukee wasn't just a city; it was a family of music enthusiasts, artists, and dreamers. Together, we created moments of pure euphoria on the dance floor, where the worries of the world faded, and all that remained was the music and the energy we shared. In those moments, I found my purpose – not just as a DJ, but as a storyteller, weaving narratives through melodies and rhythms, leaving a lasting impact on the souls that tuned into my beats.`,
  card1color: `bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200`,
  card2color: `bg-gradient-to-br from-indigo-200 via-purple-300 to-indigo-200`,
  font: `../public//fonts/Vogue.ttf`,
  cards: {
    primary: `rounded-lg bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200`, //`backdrop-blur-2xl`,
    secondary: `bg-gradient-to-br from-indigo-200 via-purple-300 to-indigo-200`, //`backdrop-blur-2xl bg-opacity-25 bg-blue-400`,
    tertiary: 'bg-slate-700 box-shadow-lg text-white border-2 border-slate-600',
  },
  forms: {
    primary: `bg-blue-500 bg-opacity-50 text-white shadow-sm`,
    secondary: `bg-slate-500 bg-opacity-50 text-white shadow-sm`,
    tertiary: `bg-pink-500 bg-opacity-50 text-white shadow-sm border-b-2 border-pink-600`,
  },
  buttons: {
    primary: `bg-slate-700 rounded-none`,
    secondary: `bg-blue-400 rounded-none`,
    tertiary: `rounded-sm bg-slate-100 bg-opacity-90 text-slate-400`
  },
  fonts: {
    primary: {
      family: 'Andale Mono'
    },
    secondary: {
      family: 'Courier New'
    },
    tertiary: {
      family: 'Arial'
    }
  },
}

export function deepMergeWithDefaults<T>(defaults: T, overrides: any): T {
  if (typeof defaults !== 'object' || defaults === null) {
    return overrides !== undefined ? overrides : defaults;
  }

  if (Array.isArray(defaults)) {
    if (!Array.isArray(overrides)) {
      return defaults;
    }

    const defaultItem = defaults[0]; // Use first item in default array as schema
    return overrides.map((overrideItem) => {
      if (typeof defaultItem === 'object' && defaultItem !== null && !Array.isArray(defaultItem)) {
        return deepMergeWithDefaults(defaultItem, overrideItem);
      } else if (Array.isArray(defaultItem)) {
        return deepMergeWithDefaults(defaultItem, overrideItem);
      } else {
        return overrideItem !== undefined ? overrideItem : defaultItem;
      }
    }) as T;
  }

  // If plain object, merge all keys from defaults
  const result: any = {};
  for (const key in defaults) {
    if (Object.prototype.hasOwnProperty.call(defaults, key)) {
      result[key] = deepMergeWithDefaults(defaults[key], overrides?.[key]);
    }
  }

  return result;
}

export const tailwind = {
  blur: {
    "blur_none": "blur-none",
    "blur_sm": "blur-sm",
    "blur_md": "blur-md",
    "blur_lg": "blur-lg",
    "blur_xl": "blur-xl",
    "blur_2xl": "blur-2xl",
    "blur_3xl": "blur-3xl",
    "blur_4xl": "blur-4xl",
    "blur_5xl": "blur-5xl",
  },
  brightness: {
    "brightness_100": "brightness-100",
    "brightness_0": "brightness-0",
    "brightness_50": "brightness-50",
    "brightness_75": "brightness-75",
    "brightness_90": "brightness-90",
    "brightness_95": "brightness-95",
    "brightness_105": "brightness-105",
    "brightness_110": "brightness-110",
    "brightness_125": "brightness-125",
    "brightness_150": "brightness-150",
    "brightness_200": "brightness-200",
  },

  contrast: {

    "contrast_100": "contrast-100",
    "contrast_0": "contrast-0",
    "contrast_50": "contrast-50",
    "contrast_75": "contrast-75",
    "contrast_125": "contrast-125",
    "contrast_150": "contrast-150",
    "contrast_200": "contrast-200",
  },
  grayscale: {
    "grayscale_0": "grayscale-0",
    "grayscale_100": "grayscale",
  },
  hue_rotate: {
    "hue_rotate_0": "hue-rotate-0",
    "hue_rotate_15": "hue-rotate-15",
    "hue_rotate_30": "hue-rotate-30",
    "hue_rotate_60": "hue-rotate-60",
    "hue_rotate_90": "hue-rotate-90",
    "hue_rotate_180": "hue-rotate-180",
    "hue_rotate_270": "hue-rotate-270",
    "hue_rotate_360": "hue-rotate-360",
  },
  invert: {
    "invert_0": "invert-0",
    "invert_100": "invert",
  },
  opacity: {
    "opacity_100": "opacity-100",
    "opacity_0": "opacity-0",
    "opacity_5": "opacity-5",
    "opacity_10": "opacity-10",
    "opacity_20": "opacity-20",
    "opacity_25": "opacity-25",
    "opacity_30": "opacity-30",
    "opacity_40": "opacity-40",
    "opacity_50": "opacity-50",
    "opacity_60": "opacity-60",
    "opacity_70": "opacity-70",
    "opacity_75": "opacity-75",
    "opacity_80": "opacity-80",
    "opacity_90": "opacity-90",
  },
  saturate: {
    "saturate_100": "saturate-100",
    "saturate_0": "saturate-0",
    "saturate_50": "saturate-50",
    "saturate_150": "saturate-150",
    "saturate_200": "saturate-200",
  },
  sepia: {
    "sepia_0": "sepia-0",
    "sepia_100": "sepia",
  },
  drop_shadow: {
    "drop_shadow_none": "drop-shadow-none",
    "drop_shadow_sm": "drop-shadow-sm",
    "drop_shadow_DEFAULT": "drop-shadow",
    "drop_shadow_md": "drop-shadow-md",
    "drop_shadow_lg": "drop-shadow-lg",
    "drop_shadow_xl": "drop-shadow-xl",
    "drop_shadow_2xl": "drop-shadow-2xl",
    "drop_shadow_3xl": "drop-shadow-3xl",
  },
  backdrop_blur: {
    "backdrop_blur_none": "backdrop-blur-none",
    "backdrop_blur_sm": "backdrop-blur-sm",
    "backdrop_blur_md": "backdrop-blur-md",
    "backdrop_blur_lg": "backdrop-blur-lg",
    "backdrop_blur_xl": "backdrop-blur-xl",
    "backdrop_blur_2xl": "backdrop-blur-2xl",
    "backdrop_blur_3xl": "backdrop-blur-3xl",
    "backdrop_blur_4xl": "backdrop-blur-4xl",
    "backdrop_blur_5xl": "backdrop-blur-5xl",
  },
  backdrop_brightness: {
    "backdrop_brightness_100": "backdrop-brightness-100",
    "backdrop_brightness_0": "backdrop-brightness-0",
    "backdrop_brightness_50": "backdrop-brightness-50",
    "backdrop_brightness_75": "backdrop-brightness-75",
    "backdrop_brightness_90": "backdrop-brightness-90",
    "backdrop_brightness_95": "backdrop-brightness-95",
    "backdrop_brightness_105": "backdrop-brightness-105",
    "backdrop_brightness_110": "backdrop-brightness-110",
    "backdrop_brightness_125": "backdrop-brightness-125",
    "backdrop_brightness_150": "backdrop-brightness-150",
    "backdrop_brightness_200": "backdrop-brightness-200",
  },
  backdrop_contrast: {
    "backdrop_contrast_100": "backdrop-contrast-100",
    "backdrop_contrast_0": "backdrop-contrast-0",
    "backdrop_contrast_50": "backdrop-contrast-50",
    "backdrop_contrast_75": "backdrop-contrast-75",
    "backdrop_contrast_125": "backdrop-contrast-125",
    "backdrop_contrast_150": "backdrop-contrast-150",
    "backdrop_contrast_200": "backdrop-contrast-200",
  },
  backdrop_grayscale: {
    "backdrop_grayscale_0": "backdrop-grayscale-0",
    "backdrop_grayscale_100": "backdrop-grayscale",
  },
  backdrop_hue_rotate: {
    "backdrop_hue_rotate_0": "backdrop-hue-rotate-0",
    "backdrop_hue_rotate_15": "backdrop-hue-rotate-15",
    "backdrop_hue_rotate_30": "backdrop-hue-rotate-30",
    "backdrop_hue_rotate_60": "backdrop-hue-rotate-60",
    "backdrop_hue_rotate_90": "backdrop-hue-rotate-90",
    "backdrop_hue_rotate_180": "backdrop-hue-rotate-180",
    "backdrop_hue_rotate_270": "backdrop-hue-rotate-270",
    "backdrop_hue_rotate_360": "backdrop-hue-rotate-360",
  },
  backdrop_invert: {
    "backdrop_invert_0": "backdrop-invert-0",
    "backdrop_invert_100": "backdrop-invert",
  },
  backdrop_opacity: {
    "backdrop_opacity_100": "backdrop-opacity-100",
    "backdrop_opacity_0": "backdrop-opacity-0",
    "backdrop_opacity_5": "backdrop-opacity-5",
    "backdrop_opacity_10": "backdrop-opacity-10",
    "backdrop_opacity_20": "backdrop-opacity-20",
    "backdrop_opacity_25": "backdrop-opacity-25",
    "backdrop_opacity_30": "backdrop-opacity-30",
    "backdrop_opacity_40": "backdrop-opacity-40",
    "backdrop_opacity_50": "backdrop-opacity-50",
    "backdrop_opacity_60": "backdrop-opacity-60",
    "backdrop_opacity_70": "backdrop-opacity-70",
    "backdrop_opacity_75": "backdrop-opacity-75",
    "backdrop_opacity_80": "backdrop-opacity-80",
    "backdrop_opacity_90": "backdrop-opacity-90",
  },
  backdrop_saturate: {
    "backdrop_saturate_100": "backdrop-saturate-100",
    "backdrop_saturate_0": "backdrop-saturate-0",
    "backdrop_saturate_50": "backdrop-saturate-50",
    "backdrop_saturate_150": "backdrop-saturate-150",
    "backdrop_saturate_200": "backdrop-saturate-200",
  },
  backdrop_sepia: {
    "backdrop_sepia_0": "backdrop-sepia-0",
    "backdrop_sepia_100": "backdrop-sepia"
  },
  text_color: {
    "text_inherit": "text-inherit",
    "text_gold": "gold-text",
    "text_rainbow": "rainbow-text",
    "text_neon_red": "neon-text-red",
    "text_neon_green": "neon-text-green",
    "text_neon_blue": "neon-text-blue",
    "text_neon": "neon-text",
    "text-current": "text-current",
    "text-transparent": "text-transparent",
    "text_black": "text-black",
    "text_white": "text-white",
    "text_slate_50": "text-slate-50",
    "text_slate_100": "text-slate-100",
    "text_slate_200": "text-slate-200",
    "text_slate_300": "text-slate-300",
    "text_slate_400": "text-slate-400",
    "text_slate_500": "text-slate-500",
    "text_slate_600": "text-slate-600",
    "text_slate_700": "text-slate-700",
    "text_slate_800": "text-slate-800",
    "text_slate_900": "text-slate-900",
    "text_gray_50": "text-gray-50",
    "text_gray_100": "text-gray-100",
    "text_gray_200": "text-gray-200",
    "text_gray_300": "text-gray-300",
    "text_gray_400": "text-gray-400",
    "text_gray_500": "text-gray-500",
    "text_gray_600": "text-gray-600",
    "text_gray_700": "text-gray-700",
    "text_gray_800": "text-gray-800",
    "text_gray_900": "text-gray-900",
    "text_neutral_50": "text-neutral-50",
    "text_neutral_100": "text-neutral-100",
    "text_neutral_200": "text-neutral-200",
    "text_neutral_300": "text-neutral-300",
    "text_neutral_400": "text-neutral-400",
    "text_neutral_500": "text-neutral-500",
    "text_neutral_600": "text-neutral-600",
    "text_neutral_700": "text-neutral-700",
    "text_neutral_800": "text-neutral-800",
    "text_neutral_900": "text-neutral-900",
    "text_zinc_50": "text-zinc-50",
    "text_zinc_100": "text-zinc-100",
    "text_zinc_200": "text-zinc-200",
    "text_zinc_300": "text-zinc-300",
    "text_zinc_400": "text-zinc-400",
    "text_zinc_500": "text-zinc-500",
    "text_zinc_600": "text-zinc-600",
    "text_zinc_700": "text-zinc-700",
    "text_zinc_800": "text-zinc-800",
    "text_zinc_900": "text-zinc-900",
    "text_amber_50": "text-amber-50",
    "text_amber_100": "text-amber-100",
    "text_amber_200": "text-amber-200",
    "text_amber_300": "text-amber-300",
    "text_amber_400": "text-amber-400",
    "text_amber_500": "text-amber-500",
    "text_amber_600": "text-amber-600",
    "text_amber_700": "text-amber-700",
    "text_amber_800": "text-amber-800",
    "text_amber_900": "text-amber-900",
    "text_yellow_50": "text-yellow-50",
    "text_yellow_100": "text-yellow-100",
    "text_yellow_200": "text-yellow-200",
    "text_yellow_300": "text-yellow-300",
    "text_yellow_400": "text-yellow-400",
    "text_yellow_500": "text-yellow-500",
    "text_yellow_600": "text-yellow-600",
    "text_yellow_700": "text-yellow-700",
    "text_yellow_800": "text-yellow-800",
    "text_yellow_900": "text-yellow-900",
    "text_lime_50": "text-lime-50",
    "text_lime_100": "text-lime-100",
    "text_lime_200": "text-lime-200",
    "text_lime_300": "text-lime-300",
    "text_lime_400": "text-lime-400",
    "text_lime_500": "text-lime-500",
    "text_lime_600": "text-lime-600",
    "text_lime_700": "text-lime-700",
    "text_lime_800": "text-lime-800",
    "text_lime_900": "text-lime-900",
    "text_green_50": "text-green-50",
    "text_green_100": "text-green-100",
    "text_green_200": "text-green-200",
    "text_green_300": "text-green-300",
    "text_green_400": "text-green-400",
    "text_green_500": "text-green-500",
    "text_green_600": "text-green-600",
    "text_green_700": "text-green-700",
    "text_green_800": "text-green-800",
    "text_green_900": "text-green-900",
    "text_emerald_50": "text-emerald-50",
    "text_emerald_100": "text-emerald-100",
    "text_emerald_200": "text-emerald-200",
    "text_emerald_300": "text-emerald-300",
    "text_emerald_400": "text-emerald-400",
    "text_emerald_500": "text-emerald-500",
    "text_emerald_600": "text-emerald-600",
    "text_emerald_700": "text-emerald-700",
    "text_emerald_800": "text-emerald-800",
    "text_emerald_900": "text-emerald-900",
    "text_teal_50": "text-teal-50",
    "text_teal_100": "text-teal-100",
    "text_teal_200": "text-teal-200",
    "text_teal_300": "text-teal-300",
    "text_teal_400": "text-teal-400",
    "text_teal_500": "text-teal-500",
    "text_teal_600": "text-teal-600",
    "text_teal_700": "text-teal-700",
    "text_teal_800": "text-teal-800",
    "text_teal_900": "text-teal-900",
    "text_cyan_50": "text-cyan-50",
    "text_cyan_100": "text-cyan-100",
    "text_cyan_200": "text-cyan-200",
    "text_cyan_300": "text-cyan-300",
    "text_cyan_400": "text-cyan-400",
    "text_cyan_500": "text-cyan-500",
    "text_cyan_600": "text-cyan-600",
    "text_cyan_700": "text-cyan-700",
    "text_cyan_800": "text-cyan-800",
    "text_cyan_900": "text-cyan-900",
    "text_sky_50": "text-sky-50",
    "text_sky_100": "text-sky-100",
    "text_sky_200": "text-sky-200",
    "text_sky_300": "text-sky-300",
    "text_sky_400": "text-sky-400",
    "text_sky_500": "text-sky-500",
    "text_sky_600": "text-sky-600",
    "text_sky_700": "text-sky-700",
    "text_sky_800": "text-sky-800",
    "text_sky_900": "text-sky-900",
    "text_blue_50": "text-blue-50",
    "text_blue_100": "text-blue-100",
    "text_blue_200": "text-blue-200",
    "text_blue_300": "text-blue-300",
    "text_blue_400": "text-blue-400",
    "text_blue_500": "text-blue-500",
    "text_blue_600": "text-blue-600",
    "text_blue_700": "text-blue-700",
    "text_blue_800": "text-blue-800",
    "text_blue_900": "text-blue-900",
    "text_indigo_50": "text-indigo-50",
    "text_indigo_100": "text-indigo-100",
    "text_indigo_200": "text-indigo-200",
    "text_indigo_300": "text-indigo-300",
    "text_indigo_400": "text-indigo-400",
    "text_indigo_500": "text-indigo-500",
    "text_indigo_600": "text-indigo-600",
    "text_indigo_700": "text-indigo-700",
    "text_indigo_800": "text-indigo-800",
    "text_indigo_900": "text-indigo-900",
    "text_violet_50": "text-violet-50",
    "text_violet_100": "text-violet-100",
    "text_violet_200": "text-violet-200",
    "text_violet_300": "text-violet-300",
    "text_violet_400": "text-violet-400",
    "text_violet_500": "text-violet-500",
    "text_violet_600": "text-violet-600",
    "text_violet_700": "text-violet-700",
    "text_violet_800": "text-violet-800",
    "text_violet_900": "text-violet-900",
    "text_purple_50": "text-purple-50",
    "text_purple_100": "text-purple-100",
    "text_purple_200": "text-purple-200",
    "text_purple_300": "text-purple-300",
    "text_purple_400": "text-purple-400",
    "text_purple_500": "text-purple-500",
    "text_purple_600": "text-purple-600",
    "text_purple_700": "text-purple-700",
    "text_purple_800": "text-purple-800",
    "text_purple_900": "text-purple-900",
    "text_fuchsia_50": "text-fuchsia-50",
    "text_fuchsia_100": "text-fuchsia-100",
    "text_fuchsia_200": "text-fuchsia-200",
    "text_fuchsia_300": "text-fuchsia-300",
    "text_fuchsia_400": "text-fuchsia-400",
    "text_fuchsia_500": "text-fuchsia-500",
    "text_fuchsia_600": "text-fuchsia-600",
    "text_fuchsia_700": "text-fuchsia-700",
    "text_fuchsia_800": "text-fuchsia-800",
    "text_fuchsia_900": "text-fuchsia-900",
    "text_rose_50": "text-rose-50",
    "text_rose_100": "text-rose-100",
    "text_rose_200": "text-rose-200",
    "text_rose_300": "text-rose-300",
    "text_rose_400": "text-rose-400",
    "text_rose_500": "text-rose-500",
    "text_rose_600": "text-rose-600",
    "text_rose_700": "text-rose-700",
    "text_rose_800": "text-rose-800",
    "text_rose_900": "text-rose-900"
  },
  background_color: {
    "bg_inherit": "bg-inherit",
    "bg_gradient_red": "bg-gradient-to-br from-red-200 via-red-300 to-red-300",
    "bg_gradient_orange": "bg-gradient-to-br from-orange-200 via-orange-300 to-orange-300",
    "bg_gradient_yellow": "bg-gradient-to-br from-yellow-200 via-yellow-300 to-yellow-300",
    "bg_gradient_green": "bg-gradient-to-br from-green-200 via-green-300 to-green-300",
    "bg_gradient_blue": "bg-gradient-to-br from-blue-200 via-blue-300 to-blue-300",
    "bg_gradient_pink": "bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200",
    "bg_gradient_purple": "bg-gradient-to-br from-indigo-200 via-purple-300 to-indigo-200",
    "bg_gradient_gold": "bg-gradient-to-br from-gold-200 via-gold-300 to-gold-300",
    "bg_gradient_white": "bg-gradient-to-br from-slate-50 via-slate-200 to-slate-100",
    "bg_gradient_black": "bg-gradient-to-br from-slate-700 via-slate-900 to-slate-700",
    "bg_current": "bg-current",
    "bg_transparent": "bg-transparent",
    "bg_black": "bg-black",
    "bg_white": "bg-white",
    "bg_slate_50": "bg-slate-50",
    "bg_slate_100": "bg-slate-100",
    "bg_slate_200": "bg-slate-200",
    "bg_slate_300": "bg-slate-300",
    "bg_slate_400": "bg-slate-400",
    "bg_slate_500": "bg-slate-500",
    "bg_slate_600": "bg-slate-600",
    "bg_slate_700": "bg-slate-700",
    "bg_slate_800": "bg-slate-800",
    "bg_slate_900": "bg-slate-900",
    "bg_gray_50": "bg-gray-50",
    "bg_gray_100": "bg-gray-100",
    "bg_gray_200": "bg-gray-200",
    "bg_gray_300": "bg-gray-300",
    "bg_gray_400": "bg-gray-400",
    "bg_gray_500": "bg-gray-500",
    "bg_gray_600": "bg-gray-600",
    "bg_gray_700": "bg-gray-700",
    "bg_gray_800": "bg-gray-800",
    "bg_gray_900": "bg-gray-900",
    "bg_neutral_50": "bg-neutral-50",
    "bg_neutral_100": "bg-neutral-100",
    "bg_neutral_200": "bg-neutral-200",
    "bg_neutral_300": "bg-neutral-300",
    "bg_neutral_400": "bg-neutral-400",
    "bg_neutral_500": "bg-neutral-500",
    "bg_neutral_600": "bg-neutral-600",
    "bg_neutral_700": "bg-neutral-700",
    "bg_neutral_800": "bg-neutral-800",
    "bg_neutral_900": "bg-neutral-900",
    "bg_zinc_50": "bg-zinc-50",
    "bg_zinc_100": "bg-zinc-100",
    "bg_zinc_200": "bg-zinc-200",
    "bg_zinc_300": "bg-zinc-300",
    "bg_zinc_400": "bg-zinc-400",
    "bg_zinc_500": "bg-zinc-500",
    "bg_zinc_600": "bg-zinc-600",
    "bg_zinc_700": "bg-zinc-700",
    "bg_zinc_800": "bg-zinc-800",
    "bg_zinc_900": "bg-zinc-900",
    "bg_amber_50": "bg-amber-50",
    "bg_amber_100": "bg-amber-100",
    "bg_amber_200": "bg-amber-200",
    "bg_amber_300": "bg-amber-300",
    "bg_amber_400": "bg-amber-400",
    "bg_amber_500": "bg-amber-500",
    "bg_amber_600": "bg-amber-600",
    "bg_amber_700": "bg-amber-700",
    "bg_amber_800": "bg-amber-800",
    "bg_amber_900": "bg-amber-900",
    "bg_yellow_50": "bg-yellow-50",
    "bg_yellow_100": "bg-yellow-100",
    "bg_yellow_200": "bg-yellow-200",
    "bg_yellow_300": "bg-yellow-300",
    "bg_yellow_400": "bg-yellow-400",
    "bg_yellow_500": "bg-yellow-500",
    "bg_yellow_600": "bg-yellow-600",
    "bg_yellow_700": "bg-yellow-700",
    "bg_yellow_800": "bg-yellow-800",
    "bg_yellow_900": "bg-yellow-900",
    "bg_lime_50": "bg-lime-50",
    "bg_lime_100": "bg-lime-100",
    "bg_lime_200": "bg-lime-200",
    "bg_lime_300": "bg-lime-300",
    "bg_lime_400": "bg-lime-400",
    "bg_lime_500": "bg-lime-500",
    "bg_lime_600": "bg-lime-600",
    "bg_lime_700": "bg-lime-700",
    "bg_lime_800": "bg-lime-800",
    "bg_lime_900": "bg-lime-900",
    "bg_green_50": "bg-green-50",
    "bg_green_100": "bg-green-100",
    "bg_green_200": "bg-green-200",
    "bg_green_300": "bg-green-300",
    "bg_green_400": "bg-green-400",
    "bg_green_500": "bg-green-500",
    "bg_green_600": "bg-green-600",
    "bg_green_700": "bg-green-700",
    "bg_green_800": "bg-green-800",
    "bg_green_900": "bg-green-900",
    "bg_emerald_50": "bg-emerald-50",
    "bg_emerald_100": "bg-emerald-100",
    "bg_emerald_200": "bg-emerald-200",
    "bg_emerald_300": "bg-emerald-300",
    "bg_emerald_400": "bg-emerald-400",
    "bg_emerald_500": "bg-emerald-500",
    "bg_emerald_600": "bg-emerald-600",
    "bg_emerald_700": "bg-emerald-700",
    "bg_emerald_800": "bg-emerald-800",
    "bg_emerald_900": "bg-emerald-900",
    "bg_teal_50": "bg-teal-50",
    "bg_teal_100": "bg-teal-100",
    "bg_teal_200": "bg-teal-200",
    "bg_teal_300": "bg-teal-300",
    "bg_teal_400": "bg-teal-400",
    "bg_teal_500": "bg-teal-500",
    "bg_teal_600": "bg-teal-600",
    "bg_teal_700": "bg-teal-700",
    "bg_teal_800": "bg-teal-800",
    "bg_teal_900": "bg-teal-900",
    "bg_cyan_50": "bg-cyan-50",
    "bg_cyan_100": "bg-cyan-100",
    "bg_cyan_200": "bg-cyan-200",
    "bg_cyan_300": "bg-cyan-300",
    "bg_cyan_400": "bg-cyan-400",
    "bg_cyan_500": "bg-cyan-500",
    "bg_cyan_600": "bg-cyan-600",
    "bg_cyan_700": "bg-cyan-700",
    "bg_cyan_800": "bg-cyan-800",
    "bg_cyan_900": "bg-cyan-900",
    "bg_sky_50": "bg-sky-50",
    "bg_sky_100": "bg-sky-100",
    "bg_sky_200": "bg-sky-200",
    "bg_sky_300": "bg-sky-300",
    "bg_sky_400": "bg-sky-400",
    "bg_sky_500": "bg-sky-500",
    "bg_sky_600": "bg-sky-600",
    "bg_sky_700": "bg-sky-700",
    "bg_sky_800": "bg-sky-800",
    "bg_sky_900": "bg-sky-900",
    "bg_blue_50": "bg-blue-50",
    "bg_blue_100": "bg-blue-100",
    "bg_blue_200": "bg-blue-200",
    "bg_blue_300": "bg-blue-300",
    "bg_blue_400": "bg-blue-400",
    "bg_blue_500": "bg-blue-500",
    "bg_blue_600": "bg-blue-600",
    "bg_blue_700": "bg-blue-700",
    "bg_blue_800": "bg-blue-800",
    "bg_blue_900": "bg-blue-900",
    "bg_indigo_50": "bg-indigo-50",
    "bg_indigo_100": "bg-indigo-100",
    "bg_indigo_200": "bg-indigo-200",
    "bg_indigo_300": "bg-indigo-300",
    "bg_indigo_400": "bg-indigo-400",
    "bg_indigo_500": "bg-indigo-500",
    "bg_indigo_600": "bg-indigo-600",
    "bg_indigo_700": "bg-indigo-700",
    "bg_indigo_800": "bg-indigo-800",
    "bg_indigo_900": "bg-indigo-900",
    "bg_violet_50": "bg-violet-50",
    "bg_violet_100": "bg-violet-100",
    "bg_violet_200": "bg-violet-200",
    "bg_violet_300": "bg-violet-300",
    "bg_violet_400": "bg-violet-400",
    "bg_violet_500": "bg-violet-500",
    "bg_violet_600": "bg-violet-600",
    "bg_violet_700": "bg-violet-700",
    "bg_violet_800": "bg-violet-800",
    "bg_violet_900": "bg-violet-900",
    "bg_purple_50": "bg-purple-50",
    "bg_purple_100": "bg-purple-100",
    "bg_purple_200": "bg-purple-200",
    "bg_purple_300": "bg-purple-300",
    "bg_purple_400": "bg-purple-400",
    "bg_purple_500": "bg-purple-500",
    "bg_purple_600": "bg-purple-600",
    "bg_purple_700": "bg-purple-700",
    "bg_purple_800": "bg-purple-800",
    "bg_purple_900": "bg-purple-900",
    "bg_fuchsia_50": "bg-fuchsia-50",
    "bg_fuchsia_100": "bg-fuchsia-100",
    "bg_fuchsia_200": "bg-fuchsia-200",
    "bg_fuchsia_300": "bg-fuchsia-300",
    "bg_fuchsia_400": "bg-fuchsia-400",
    "bg_fuchsia_500": "bg-fuchsia-500",
    "bg_fuchsia_600": "bg-fuchsia-600",
    "bg_fuchsia_700": "bg-fuchsia-700",
    "bg_fuchsia_800": "bg-fuchsia-800",
    "bg_fuchsia_900": "bg-fuchsia-900",
    "bg_rose_50": "bg-rose-50",
    "bg_rose_100": "bg-rose-100",
    "bg_rose_200": "bg-rose-200",
    "bg_rose_300": "bg-rose-300",
    "bg_rose_400": "bg-rose-400",
    "bg_rose_500": "bg-rose-500",
    "bg_rose_600": "bg-rose-600",
    "bg_rose_700": "bg-rose-700",
    "bg_rose_800": "bg-rose-800",
    "bg_rose_900": "bg-rose-900"
  },
  background_opacity: {
    "bg_opacity_100": "bg-opacity-100",
    "bg_opacity_0": "bg-opacity-0",
    "bg_opacity_5": "bg-opacity-5",
    "bg_opacity_10": "bg-opacity-10",
    "bg_opacity_20": "bg-opacity-20",
    "bg_opacity_25": "bg-opacity-25",
    "bg_opacity_30": "bg-opacity-30",
    "bg_opacity_40": "bg-opacity-40",
    "bg_opacity_50": "bg-opacity-50",
    "bg_opacity_60": "bg-opacity-60",
    "bg_opacity_70": "bg-opacity-70",
    "bg_opacity_75": "bg-opacity-75",
    "bg_opacity_80": "bg-opacity-80",
    "bg_opacity_90": "bg-opacity-90",
  },
  border_radius: {
    "rounded_none": "rounded-none",
    "rounded_sm": "rounded-sm",
    "rounded": "rounded",
    "rounded_md": "rounded-md",
    "rounded_lg": "rounded-lg",
    "rounded_xl": "rounded-xl",
    "rounded_2xl": "rounded-2xl",
    "rounded_3xl": "rounded-3xl",
    "rounded_full": "rounded-full",
    "rounded_tl_none": "rounded-tl-none",
    "rounded_tr_none": "rounded-tr-none",
    "rounded_br_none": "rounded-br-none",
    "rounded_bl_none": "rounded-bl-none",
    "rounded_tl_sm": "rounded-tl-sm",
    "rounded_tr_sm": "rounded-tr-sm",
    "rounded_br_sm": "rounded-br-sm",
    "rounded_bl_sm": "rounded-bl-sm",
    "rounded_tl": "rounded-tl",
    "rounded_tr": "rounded-tr",
    "rounded_br": "rounded-br",
    "rounded_bl": "rounded-bl",
    "rounded_tl_md": "rounded-tl-md",
    "rounded_tr_md": "rounded-tr-md",
    "rounded_br_md": "rounded-br-md",
    "rounded_bl_md": "rounded-bl-md",
    "rounded_tl_lg": "rounded-tl-lg",
    "rounded_tr_lg": "rounded-tr-lg",
    "rounded_br_lg": "rounded-br-lg",
    "rounded_bl_lg": "rounded-bl-lg",
    "rounded_tl_xl": "rounded-tl-xl",
    "rounded_tr_xl": "rounded-tr-xl",
    "rounded_br_xl": "rounded-br-xl",
    "rounded_bl_xl": "rounded-bl-xl",
    "rounded_tl_2xl": "rounded-tl-2xl",
    "rounded_tr_2xl": "rounded-tr-2xl",
    "rounded_br_2xl": "rounded-br-2xl",
    "rounded_bl_2xl": "rounded-bl-2xl",
    "rounded_tl_3xl": "rounded-tl-3xl",
    "rounded_tr_3xl": "rounded-tr-3xl",
    "rounded_br_3xl": "rounded-br-3xl",
    "rounded_bl_3xl": "rounded-bl-3xl",
    "rounded_tl_full": "rounded-tl-full",
    "rounded_tr_full": "rounded-tr-full",
    "rounded_br_full": "rounded-br-full",
    "rounded_bl_full": "rounded-bl-full"
  },
  border_width: {
    "border_0": "border-0",
    "border_2": "border-2",
    "border_4": "border-4",
    "border_8": "border-8",
    "border": "border",
    "border_t_0": "border-t-0",
    "border_t_2": "border-t-2",
    "border_t_4": "border-t-4",
    "border_t_8": "border-t-8",
    "border_t": "border-t",
    "border_r_0": "border-r-0",
    "border_r_2": "border-r-2",
    "border_r_4": "border-r-4",
    "border_r_8": "border-r-8",
    "border_r": "border-r",
    "border_b_0": "border-b-0",
    "border_b_2": "border-b-2",
    "border_b_4": "border-b-4",
    "border_b_8": "border-b-8",
    "border_b": "border-b",
    "border_l_0": "border-l-0",
    "border_l_2": "border-l-2",
    "border_l_4": "border-l-4",
    "border_l_8": "border-l-8",
    "border_l": "border-l"
  },
  border_color: {
    "border_inherit": "border-inherit",
    "border_current": "border-current",
    "border_transparent": "border-transparent",
    "border_black": "border-black",
    "border_white": "border-white",
    "border_slate_50": "border-slate-50",
    "border_slate_100": "border-slate-100",
    "border_slate_200": "border-slate-200",
    "border_slate_300": "border-slate-300",
    "border_slate_400": "border-slate-400",
    "border_slate_500": "border-slate-500",
    "border_slate_600": "border-slate-600",
    "border_slate_700": "border-slate-700",
    "border_slate_800": "border-slate-800",
    "border_slate_900": "border-slate-900",
    "border_gray_50": "border-gray-50",
    "border_gray_100": "border-gray-100",
    "border_gray_200": "border-gray-200",
    "border_gray_300": "border-gray-300",
    "border_gray_400": "border-gray-400",
    "border_gray_500": "border-gray-500",
    "border_gray_600": "border-gray-600",
    "border_gray_700": "border-gray-700",
    "border_gray_800": "border-gray-800",
    "border_gray_900": "border-gray-900",
    "border_neutral_50": "border-neutral-50",
    "border_neutral_100": "border-neutral-100",
    "border_neutral_200": "border-neutral-200",
    "border_neutral_300": "border-neutral-300",
    "border_neutral_400": "border-neutral-400",
    "border_neutral_500": "border-neutral-500",
    "border_neutral_600": "border-neutral-600",
    "border_neutral_700": "border-neutral-700",
    "border_neutral_800": "border-neutral-800",
    "border_neutral_900": "border-neutral-900",
    "border_zinc_50": "border-zinc-50",
    "border_zinc_100": "border-zinc-100",
    "border_zinc_200": "border-zinc-200",
    "border_zinc_300": "border-zinc-300",
    "border_zinc_400": "border-zinc-400",
    "border_zinc_500": "border-zinc-500",
    "border_zinc_600": "border-zinc-600",
    "border_zinc_700": "border-zinc-700",
    "border_zinc_800": "border-zinc-800",
    "border_zinc_900": "border-zinc-900",
    "border_amber_50": "border-amber-50",
    "border_amber_100": "border-amber-100",
    "border_amber_200": "border-amber-200",
    "border_amber_300": "border-amber-300",
    "border_amber_400": "border-amber-400",
    "border_amber_500": "border-amber-500",
    "border_amber_600": "border-amber-600",
    "border_amber_700": "border-amber-700",
    "border_amber_800": "border-amber-800",
    "border_amber_900": "border-amber-900",
    "border_yellow_50": "border-yellow-50",
    "border_yellow_100": "border-yellow-100",
    "border_yellow_200": "border-yellow-200",
    "border_yellow_300": "border-yellow-300",
    "border_yellow_400": "border-yellow-400",
    "border_yellow_500": "border-yellow-500",
    "border_yellow_600": "border-yellow-600",
    "border_yellow_700": "border-yellow-700",
    "border_yellow_800": "border-yellow-800",
    "border_yellow_900": "border-yellow-900",
    "border_lime_50": "border-lime-50",
    "border_lime_100": "border-lime-100",
    "border_lime_200": "border-lime-200",
    "border_lime_300": "border-lime-300",
    "border_lime_400": "border-lime-400",
    "border_lime_500": "border-lime-500",
    "border_lime_600": "border-lime-600",
    "border_lime_700": "border-lime-700",
    "border_lime_800": "border-lime-800",
    "border_lime_900": "border-lime-900",
    "border_green_50": "border-green-50",
    "border_green_100": "border-green-100",
    "border_green_200": "border-green-200",
    "border_green_300": "border-green-300",
    "border_green_400": "border-green-400",
    "border_green_500": "border-green-500",
    "border_green_600": "border-green-600",
    "border_green_700": "border-green-700",
    "border_green_800": "border-green-800",
    "border_green_900": "border-green-900",
    "border_emerald_50": "border-emerald-50",
    "border_emerald_100": "border-emerald-100",
    "border_emerald_200": "border-emerald-200",
    "border_emerald_300": "border-emerald-300",
    "border_emerald_400": "border-emerald-400",
    "border_emerald_500": "border-emerald-500",
    "border_emerald_600": "border-emerald-600",
    "border_emerald_700": "border-emerald-700",
    "border_emerald_800": "border-emerald-800",
    "border_emerald_900": "border-emerald-900",
    "border_teal_50": "border-teal-50",
    "border_teal_100": "border-teal-100",
    "border_teal_200": "border-teal-200",
    "border_teal_300": "border-teal-300",
    "border_teal_400": "border-teal-400",
    "border_teal_500": "border-teal-500",
    "border_teal_600": "border-teal-600",
    "border_teal_700": "border-teal-700",
    "border_teal_800": "border-teal-800",
    "border_teal_900": "border-teal-900",
    "border_cyan_50": "border-cyan-50",
    "border_cyan_100": "border-cyan-100",
    "border_cyan_200": "border-cyan-200",
    "border_cyan_300": "border-cyan-300",
    "border_cyan_400": "border-cyan-400",
    "border_cyan_500": "border-cyan-500",
    "border_cyan_600": "border-cyan-600",
    "border_cyan_700": "border-cyan-700",
    "border_cyan_800": "border-cyan-800",
    "border_cyan_900": "border-cyan-900",
    "border_sky_50": "border-sky-50",
    "border_sky_100": "border-sky-100",
    "border_sky_200": "border-sky-200",
    "border_sky_300": "border-sky-300",
    "border_sky_400": "border-sky-400",
    "border_sky_500": "border-sky-500",
    "border_sky_600": "border-sky-600",
    "border_sky_700": "border-sky-700",
    "border_sky_800": "border-sky-800",
    "border_sky_900": "border-sky-900",
    "border_blue_50": "border-blue-50",
    "border_blue_100": "border-blue-100",
    "border_blue_200": "border-blue-200",
    "border_blue_300": "border-blue-300",
    "border_blue_400": "border-blue-400",
    "border_blue_500": "border-blue-500",
    "border_blue_600": "border-blue-600",
    "border_blue_700": "border-blue-700",
    "border_blue_800": "border-blue-800",
    "border_blue_900": "border-blue-900",
    "border_indigo_50": "border-indigo-50",
    "border_indigo_100": "border-indigo-100",
    "border_indigo_200": "border-indigo-200",
    "border_indigo_300": "border-indigo-300",
    "border_indigo_400": "border-indigo-400",
    "border_indigo_500": "border-indigo-500",
    "border_indigo_600": "border-indigo-600",
    "border_indigo_700": "border-indigo-700",
    "border_indigo_800": "border-indigo-800",
    "border_indigo_900": "border-indigo-900",
    "border_violet_50": "border-violet-50",
    "border_violet_100": "border-violet-100",
    "border_violet_200": "border-violet-200",
    "border_violet_300": "border-violet-300",
    "border_violet_400": "border-violet-400",
    "border_violet_500": "border-violet-500",
    "border_violet_600": "border-violet-600",
    "border_violet_700": "border-violet-700",
    "border_violet_800": "border-violet-800",
    "border_violet_900": "border-violet-900",
    "border_purple_50": "border-purple-50",
    "border_purple_100": "border-purple-100",
    "border_purple_200": "border-purple-200",
    "border_purple_300": "border-purple-300",
    "border_purple_400": "border-purple-400",
    "border_purple_500": "border-purple-500",
    "border_purple_600": "border-purple-600",
    "border_purple_700": "border-purple-700",
    "border_purple_800": "border-purple-800",
    "border_purple_900": "border-purple-900",
    "border_fuchsia_50": "border-fuchsia-50",
    "border_fuchsia_100": "border-fuchsia-100",
    "border_fuchsia_200": "border-fuchsia-200",
    "border_fuchsia_300": "border-fuchsia-300",
    "border_fuchsia_400": "border-fuchsia-400",
    "border_fuchsia_500": "border-fuchsia-500",
    "border_fuchsia_600": "border-fuchsia-600",
    "border_fuchsia_700": "border-fuchsia-700",
    "border_fuchsia_800": "border-fuchsia-800",
    "border_fuchsia_900": "border-fuchsia-900",
    "border_rose_50": "border-rose-50",
    "border_rose_100": "border-rose-100",
    "border_rose_200": "border-rose-200",
    "border_rose_300": "border-rose-300",
    "border_rose_400": "border-rose-400",
    "border_rose_500": "border-rose-500",
    "border_rose_600": "border-rose-600",
    "border_rose_700": "border-rose-700",
    "border_rose_800": "border-rose-800",
    "border_rose_900": "border-rose-900"
  },
  border_opacity: {
    "border_opacity_100": "border-opacity-100",
    "border_opacity_0": "border-opacity-0",
    "border_opacity_5": "border-opacity-5",
    "border_opacity_10": "border-opacity-10",
    "border_opacity_20": "border-opacity-20",
    "border_opacity_25": "border-opacity-25",
    "border_opacity_30": "border-opacity-30",
    "border_opacity_40": "border-opacity-40",
    "border_opacity_50": "border-opacity-50",
    "border_opacity_60": "border-opacity-60",
    "border_opacity_70": "border-opacity-70",
    "border_opacity_75": "border-opacity-75",
    "border_opacity_80": "border-opacity-80",
    "border_opacity_90": "border-opacity-90",
    "border_opacity_95": "border-opacity-95",
  },
  border_style: {
    "border_style_none": "border-none",
    "border_style_solid": "border-solid",
    "border_style_dashed": "border-dashed",
    "border_style_dotted": "border-dotted",
    "border_style_double": "border-double",
    "border_style_groove": "border-groove",
    "border_style_ridge": "border-ridge",
    "border_style_inset": "border-inset",
    "border_style_outset": "border-outset",
    "border_style_hidden": "border-hidden"
  },
  box_shadow: {
    "shadow_none": "shadow-none",
    "shadow_sm": "shadow-sm",
    "shadow": "shadow",
    "shadow_md": "shadow-md",
    "shadow_lg": "shadow-lg",
    "shadow_xl": "shadow-xl",
    "shadow_2xl": "shadow-2xl",
    "shadow_inner": "shadow-inner",
  },
  mix_blend_mode: {
    "mix_blend_normal": "mix-blend-normal",
    "mix_blend_multiply": "mix-blend-multiply",
    "mix_blend_screen": "mix-blend-screen",
    "mix_blend_overlay": "mix-blend-overlay",
    "mix_blend_darken": "mix-blend-darken",
    "mix_blend_lighten": "mix-blend-lighten",
    "mix_blend_color_dodge": "mix-blend-color-dodge",
    "mix_blend_color_burn": "mix-blend-color-burn",
    "mix_blend_hard_light": "mix-blend-hard-light",
    "mix_blend_soft_light": "mix-blend-soft-light",
    "mix_blend_difference": "mix-blend-difference",
    "mix_blend_exclusion": "mix-blend-exclusion",
    "mix_blend_HSL_hue": "mix-blend-hue",
    "mix_blend_HSL_saturation": "mix-blend-saturation",
    "mix_blend_HSL_color": "mix-blend-color",
    "mix_blend_HSL_luminosity": "mix-blend-luminosity"
  },
  background_blend_mode: {
    "bg_blend_normal": "bg-blend-normal",
    "bg_blend_multiply": "bg-blend-multiply",
    "bg_blend_screen": "bg-blend-screen",
    "bg_blend_overlay": "bg-blend-overlay",
    "bg_blend_darken": "bg-blend-darken",
    "bg_blend_lighten": "bg-blend-lighten",
    "bg_blend_color_dodge": "bg-blend-color-dodge",
    "bg_blend_color_burn": "bg-blend-color-burn",
    "bg_blend_hard_light": "bg-blend-hard-light",
    "bg_blend_soft_light": "bg-blend-soft-light",
    "bg_blend_difference": "bg-blend-difference",
    "bg_blend_exclusion": "bg-blend-exclusion",
    "bg_blend_HSL_hue": "bg-blend-hue",
    "bg_blend_HSL_saturation": "bg-blend-saturation",
    "bg_blend_HSL_color": "bg-blend-color",
    "bg_blend_HSL_luminosity": "bg-blend-luminosity"
  } 
}


type JSONObject = { [key: string]: any };
type InvertedObject = { [leafValue: string]: string[] };

function invertJson(obj: JSONObject): InvertedObject {
    const inverted: InvertedObject = {};

    function recurse(current: any, path: string[] = []): void {
        if (current !== null && typeof current === 'object' && !Array.isArray(current)) {
            for (const key in current) {
                if (current.hasOwnProperty(key)) {
                    recurse(current[key], [...path, key]);
                }
            }
        } else {
            // Leaf node reached
            inverted[String(current)] = path;
        }
    }

    recurse(obj);
    return inverted;
}

function invertStringArrayMap(input: { [key: string]: string[] }): { [value: string]: string } {
  const reverse: { [value: string]: string } = {};

  for (const [key, values] of Object.entries(input)) {
    for (const value of values) {
      reverse[value] = key;
    }
  }

  return reverse;
}
// export const invertedTailwind = invertStringArrayMap(tailwind);

export const invertedTailwind = invertJson(tailwind);